import { useIntl } from "react-intl";
import { getContrastingColor } from "../utils/color";
import { useState } from "react";
import { LoadingSpinner } from "./common/LoadingSpinner";
import { SurveyData } from "../types/survey";
import settings from "../settings";
import { MediaSkeleton } from "./common/MediaSkeleton";

interface PropTypes {
  imageDesktopUrl: string;
  imageTabletUrl: string;
  imageUrl: string;
  showMainContent: boolean;
  showSurvey: boolean;
  setShowSurvey: any;
  surveyData: any;
  responsiveImageUrl: string;
  isImageLoading: boolean;
  contentCountdown: number;
  ReactGA: any;
  adSettings?: any;
  onDataTracking: (
    clickNumber: number,
    activityType: string,
    activityDetails: string,
  ) => void;
  onSubmit: () => void;
  onSubmitSurvey: (surveyData: SurveyData) => void;
  isLogoLoading: boolean;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const Survey = ({
  imageDesktopUrl,
  imageTabletUrl,
  imageUrl,
  showMainContent,
  showSurvey,
  setShowSurvey,
  surveyData,
  responsiveImageUrl,
  isImageLoading,
  contentCountdown,
  ReactGA,
  adSettings,
  onDataTracking,
  onSubmit,
  onSubmitSurvey,
  onInputChange,
}: PropTypes) => {
  const intl = useIntl();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Hàm validate phone number (10-11 số, bắt đầu bằng đầu số Việt Nam)
  // const isValidPhoneNumber = (phone: string) => {
  //   return phone.length === 10;
  // };

  const isFormValid = () => {
    const requiredFields: string[] = [];

    if (adSettings?.fullname === "true" && !surveyData.fullName?.trim()) {
      requiredFields.push(
        intl.formatMessage({ id: "adRenderer.fullName" }) + " is required",
      );
    } else if (
      adSettings?.fullname === "true" &&
      surveyData.fullName?.trim().length < 2
    ) {
      requiredFields.push(
        `${intl.formatMessage({
          id: "adRenderer.fullName",
        })} ${intl.formatMessage({
          id: "adRenderer.validation.minLength",
        })}`,
      );
    }

    if (adSettings?.phone_number === "true" && !surveyData.phoneNumber) {
      requiredFields.push(
        intl.formatMessage({ id: "adRenderer.phoneNumber" }) + " is required",
      );
    }
    // else if (adSettings?.phone_number === "true" && !isValidPhoneNumber(surveyData.phoneNumber)) {
    //   requiredFields.push(
    //     `${intl.formatMessage({
    //       id: "adRenderer.phoneNumber",
    //     })} ${intl.formatMessage({
    //       id: "adRenderer.validation.invalidPhone",
    //     })}`
    //   );
    // }

    if (adSettings?.email === "true" && !surveyData.email) {
      requiredFields.push(
        intl.formatMessage({ id: "adRenderer.email" }) + " is required",
      );
    } else if (
      adSettings?.email === "true" &&
      !isValidEmail(surveyData.email)
    ) {
      requiredFields.push(
        `${intl.formatMessage({
          id: "adRenderer.email",
        })} ${intl.formatMessage({
          id: "adRenderer.validation.invalidEmail",
        })}`,
      );
    }

    if (adSettings?.BoD === "true" && !surveyData.birthdate) {
      requiredFields.push(
        intl.formatMessage({ id: "adRenderer.birthdate" }) + " is required",
      );
    }

    if (adSettings?.gender === "true" && !surveyData.gender) {
      requiredFields.push(
        intl.formatMessage({ id: "adRenderer.gender" }) + " is required",
      );
    }

    return requiredFields;
  };

  const getResponsiveBackground = () => {
    const screenWidth = window.innerWidth;

    const mobileBreakpoint = 768; // Mobile: < 768px
    const tabletBreakpoint = 1025; // Tablet: >= 768px and < 1024px

    if (screenWidth >= tabletBreakpoint) {
      return settings.defaultBgSurveyDesktop; // Desktop
    } else if (screenWidth >= mobileBreakpoint) {
      return settings.defaultBgSurveyTablet; // Tablet
    } else {
      return settings.defaultBgSurveyMobile; // Mobile or fallback
    }
  };

  const handleSubmitSurvey = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = isFormValid();
    if (errors.length === 0 && adSettings) {
      setValidationErrors([]);
      onSubmit();
      onSubmitSurvey({ ...surveyData });
      onDataTracking(1, "click", "User click access");
      ReactGA.event({
        category: "Survey",
        action: "Submit Survey",
        label: "Survey Form",
      });
    } else {
      setValidationErrors(errors);
    }
  };

  return (
    <>
      {!showSurvey ? (
        <div
          className={`w-full h-full flex flex-col xl:flex-row overflow-hidden transition-all duration-500 ease-in-out ${
            showMainContent
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-full pointer-events-none"
          }`}
          style={{ backgroundColor: adSettings?.background_color }}
        >
          {/* Media Section */}
          <div className="flex-1 min-h-0 flex flex-col justify-center items-center relative">
            {isImageLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-full h-full lg:h-auto aspect-video shadow-sm overflow-hidden max-h-full bg-gray-200">
                  <MediaSkeleton className="bg-transparent" />
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center overflow-hidden slide-in-right">
                <img
                  src={
                    responsiveImageUrl || require("../assets/company_logo.jpg")
                  }
                  alt="Banner"
                  className="w-full h-full object-contain max-h-full"
                />
              </div>
            )}
          </div>

          {/* Button Section */}
          <div className="w-full xl:w-[450px] flex-shrink-0 p-4 xl:p-8 flex flex-col items-center justify-center xl:border-l xl:border-gray-100/10 bg-white/5">
            <button
              onClick={() => {
                setShowSurvey(true);
              }}
              disabled={contentCountdown > 0}
              className="w-full max-w-sm px-5 py-3 md:px-8 md:py-4 rounded-full font-bold uppercase text-base md:text-lg tracking-wide disabled:opacity-75 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 animated-button"
              style={{
                backgroundColor: adSettings?.button_color || "#e60000",
                color: getContrastingColor(
                  adSettings?.button_color || "#e60000",
                ),
              }}
            >
              {(intl.locale === "en"
                ? adSettings?.button_text_en
                : adSettings?.button_text) ||
                intl.formatMessage({
                  id: "adRenderer.connectInternet",
                })}
              {contentCountdown > 0 && ` (${contentCountdown}s)`}
            </button>
          </div>
        </div>
      ) : (
        <div className="relative w-full h-full overflow-y-auto">
          <img
            src={getResponsiveBackground()}
            alt="bg-img"
            className="fixed top-0 left-0 w-full h-full object-cover"
          />
          <div className="relative z-10 flex items-center justify-center min-h-full py-10">
            <div className="w-full max-w-md mx-auto">
              <form
                id="surveyForm"
                onSubmit={handleSubmitSurvey}
                className="bg-white/70 backdrop-blur-[2px] p-5 shadow-xl w-full"
              >
                <div className="text-center uppercase font-bold text-xl">
                  {adSettings?.title_survey ||
                    intl.formatMessage({ id: "adRenderer.surveyTitle" })}
                </div>

                <div className="text-left text-md my-3">
                  {adSettings?.subtitle_survey ||
                    intl.formatMessage({
                      id: "adRenderer.surveyInstruction",
                    })}
                </div>

                <div className="py-4">
                  {adSettings?.fullname === "true" && (
                    <div className="mb-4">
                      <label
                        htmlFor="fullName"
                        className="text-left block font-semibold mb-1"
                      >
                        {intl.formatMessage({ id: "adRenderer.fullName" })}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={surveyData.fullName}
                        onChange={onInputChange}
                        placeholder={intl.formatMessage({
                          id: "adRenderer.fullNamePlaceholder",
                        })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white placeholder-gray-400 text-gray-800"
                        required
                      />
                    </div>
                  )}

                  {adSettings?.phone_number === "true" && (
                    <div className="mb-4">
                      <label
                        htmlFor="phoneNumber"
                        className="text-left block font-semibold mb-1"
                      >
                        {intl.formatMessage({ id: "adRenderer.phoneNumber" })}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={surveyData.phoneNumber}
                        maxLength={10}
                        minLength={1}
                        onChange={onInputChange}
                        placeholder={intl.formatMessage({
                          id: "adRenderer.phoneNumberPlaceholder",
                        })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white placeholder-gray-400 text-gray-800"
                        required
                      />
                    </div>
                  )}

                  {adSettings?.email === "true" && (
                    <div className="mb-4">
                      <label
                        htmlFor="email"
                        className="text-left block font-semibold mb-1"
                      >
                        {intl.formatMessage({ id: "adRenderer.email" })}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={surveyData.email}
                        onChange={onInputChange}
                        placeholder={intl.formatMessage({
                          id: "adRenderer.emailPlaceholder",
                        })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white placeholder-gray-400 text-gray-800"
                        required
                      />
                    </div>
                  )}

                  {adSettings?.BoD === "true" && (
                    <div className="mb-4">
                      <label
                        htmlFor="birthdate"
                        className="text-left block font-semibold mb-1"
                      >
                        {intl.formatMessage({ id: "adRenderer.birthdate" })}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        id="birthdate"
                        name="birthdate"
                        value={surveyData.birthdate}
                        onChange={onInputChange}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-300 bg-white"
                        required
                      />
                    </div>
                  )}

                  {adSettings?.gender === "true" && (
                    <div className="space-y-2 mb-4">
                      <fieldset>
                        <legend className="block font-semibold text-gray-700 mb-3">
                          {intl.formatMessage({ id: "adRenderer.gender" })}{" "}
                          <span className="text-red-500">*</span>
                        </legend>
                        <div className="grid grid-cols-1 gap-3">
                          {["Nam", "Nữ", "Chưa rõ"].map((g) => (
                            <label
                              key={g}
                              className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 cursor-pointer group"
                            >
                              <input
                                type="radio"
                                name="gender"
                                value={g}
                                checked={surveyData.gender === g}
                                onChange={onInputChange}
                                className="w-4 h-4 text-blue-600 border-2 border-gray-300 focus:ring-blue-500 focus:ring-2"
                                required
                              />
                              <span className="ml-3 text-gray-700 group-hover:text-blue-700 transition-colors duration-200">
                                {g === "Nam"
                                  ? intl.formatMessage({
                                      id: "adRenderer.male",
                                    })
                                  : g === "Nữ"
                                    ? intl.formatMessage({
                                        id: "adRenderer.female",
                                      })
                                    : intl.formatMessage({
                                        id: "adRenderer.other",
                                      })}
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    </div>
                  )}

                  {adSettings?.is_rating === "true" && (
                    <div className="mb-4">
                      <label
                        htmlFor="rating"
                        className="text-left block font-semibold mb-2 text-gray-700"
                      >
                        {adSettings?.rating_title ||
                          intl.formatMessage({
                            id: "adRenderer.ratingTitle",
                          })}{" "}
                        <span className="text-red-500">*</span>
                      </label>

                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => {
                              const fakeEvent = {
                                target: {
                                  name: "ratingScore",
                                  value: star.toString(),
                                },
                              } as unknown as React.ChangeEvent<HTMLInputElement>;
                              onInputChange(fakeEvent);
                            }}
                            className={`text-3xl transition-all duration-200 ${
                              Number(surveyData.ratingScore) >= star
                                ? "text-yellow-400 scale-110"
                                : "text-gray-500 hover:text-yellow-400"
                            }`}
                          >
                            ★
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {validationErrors.length > 0 && (
                  <div className="mb-4 text-red-500 text-sm">
                    <ul className="list-disc pl-5">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="text-left text-md my-3 italic">
                  {intl.formatMessage({ id: "adRenderer.privacyNotice" })}
                </div>

                <button
                  type="submit"
                  className="relative w-full py-3 rounded-xl font-bold uppercase text-lg tracking-wide text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${
                      adSettings?.button_color || "#0065b2"
                    }, ${adSettings?.button_color || "#0065b2"}dd)`,
                  }}
                >
                  {intl.formatMessage({ id: "adRenderer.confirm" })}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default Survey;
